import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { buildAdminTree, deepMerge } from './admin';

// Admin translations
const resources = {
  en: {
    translation: {
      // Navigation
      nav: {
        dashboard: 'Dashboard',
        appointments: 'Appointments',
        calendar: 'Calendar',
        providers: 'Providers',
        procedures: 'Procedures',
        operatories: 'Operatories',
        reports: 'Reports',
        settings: 'Settings',
        logout: 'Logout'
      },
      
      // Authentication
      auth: {
        login: 'Login',
        username: 'Username',
        password: 'Password',
        loginButton: 'Sign In',
        loginError: 'Invalid username or password',
        welcome: 'Welcome to SeaDent Admin',
        pleaseLogin: 'Please sign in to continue'
      },
      
      // Calendar
      calendar: {
        title: 'Appointment Calendar',
        month: 'Month',
        week: 'Week',
        day: 'Day',
        today: 'Today',
        previous: 'Previous',
        next: 'Next',
        noEvents: 'No appointments',
        filterBy: 'Filter by',
        allProviders: 'All Providers',
        allProcedures: 'All Procedures',
        allOperatories: 'All Operatories',
        refresh: 'Refresh'
      },
      
      // Appointments
      appointments: {
        title: 'Appointments',
        patient: 'Patient',
        provider: 'Provider',
        procedure: 'Procedure',
        operatory: 'Operatory',
        date: 'Date',
        time: 'Time',
        status: 'Status',
        notes: 'Notes',
        language: 'Language',
        actions: 'Actions',
        edit: 'Edit',
        cancel: 'Cancel',
        delete: 'Delete',
        view: 'View Details',
        create: 'Create Appointment',
        update: 'Update Appointment',
        confirm: 'Confirm',
        completed: 'Mark Completed',
        noShow: 'Mark No-Show'
      },

      // Quick appointment capture
      quick: {
        nav: 'Quick appointment',
        title: 'New appointment',
        subtitle: 'Create an appointment in seconds',
        patientName: 'Patient name',
        patientNamePlaceholder: 'e.g. Maria García',
        date: 'Date',
        time: 'Time',
        today: 'Today',
        tomorrow: 'Tomorrow',
        dayAfter: 'In 2 days',
        language: 'Patient language',
        moreOptions: 'More options',
        lessOptions: 'Fewer options',
        provider: 'Practitioner',
        procedure: 'Treatment',
        operatory: 'Room',
        email: 'Email (optional)',
        phone: 'Phone (optional)',
        notes: 'Note (optional)',
        notesPlaceholder: 'Anything to remember…',
        save: 'Save appointment',
        saving: 'Saving…',
        successTitle: 'Appointment saved!',
        another: 'Add another',
        done: 'Done',
        durationHint: '{{minutes}} min · ends {{end}}',
        errMissingName: 'Please enter the patient name',
        errNoDefaults: 'Please create a practitioner, treatment and room first',
        errPastDate: 'The date cannot be in the past'
      },

      // Status values
      status: {
        scheduled: 'Scheduled',
        confirmed: 'Confirmed',
        cancelled: 'Cancelled',
        completed: 'Completed',
        no_show: 'No Show'
      },
      
      // Providers
      providers: {
        title: 'Providers',
        name: 'Name',
        email: 'Email',
        phone: 'Phone',
        specialization: 'Specialization',
        active: 'Active',
        appointments: 'Appointments',
        addProvider: 'Add Provider',
        editProvider: 'Edit Provider'
      },
      
      // Procedures
      procedures: {
        title: 'Procedures',
        name: 'Name',
        description: 'Description',
        duration: 'Duration (min)',
        color: 'Color',
        active: 'Active',
        addProcedure: 'Add Procedure',
        editProcedure: 'Edit Procedure'
      },
      
      // Operatories
      operatories: {
        title: 'Operatories',
        name: 'Name',
        description: 'Description',
        active: 'Active',
        availability: 'Availability',
        addOperatory: 'Add Operatory',
        editOperatory: 'Edit Operatory'
      },
      
      // Common
      common: {
        save: 'Save',
        cancel: 'Cancel',
        delete: 'Delete',
        edit: 'Edit',
        view: 'View',
        add: 'Add',
        search: 'Search',
        filter: 'Filter',
        clear: 'Clear',
        loading: 'Loading...',
        error: 'Error',
        success: 'Success',
        confirm: 'Confirm',
        yes: 'Yes',
        no: 'No',
        close: 'Close',
        back: 'Back',
        next: 'Next',
        previous: 'Previous',
        selectDate: 'Select Date',
        selectTime: 'Select Time',
        required: 'Required',
        optional: 'Optional'
      },
      
      // Messages
      messages: {
        appointmentCreated: 'Appointment created successfully',
        appointmentUpdated: 'Appointment updated successfully',
        appointmentCancelled: 'Appointment cancelled successfully',
        appointmentDeleted: 'Appointment deleted successfully',
        confirmDelete: 'Are you sure you want to delete this appointment?',
        confirmCancel: 'Are you sure you want to cancel this appointment?',
        googleCalendarSync: 'Syncing with Google Calendar...',
        googleCalendarSynced: 'Google Calendar synchronized successfully',
        conflictDetected: 'Scheduling conflict detected',
        noConflicts: 'No conflicts found'
      }
    }
  },
  de: {
    translation: {
      nav: {
        dashboard: 'Dashboard',
        appointments: 'Termine',
        calendar: 'Kalender',
        providers: 'Behandler',
        procedures: 'Behandlungen',
        operatories: 'Räume',
        reports: 'Berichte',
        settings: 'Einstellungen',
        logout: 'Abmelden'
      },
      
      auth: {
        login: 'Anmelden',
        username: 'Benutzername',
        password: 'Passwort',
        loginButton: 'Anmelden',
        loginError: 'Ungültiger Benutzername oder Passwort',
        welcome: 'Willkommen bei SeaDent Admin',
        pleaseLogin: 'Bitte melden Sie sich an, um fortzufahren'
      },
      
      calendar: {
        title: 'Terminkalender',
        month: 'Monat',
        week: 'Woche',
        day: 'Tag',
        today: 'Heute',
        previous: 'Vorherige',
        next: 'Nächste',
        noEvents: 'Keine Termine',
        filterBy: 'Filtern nach',
        allProviders: 'Alle Behandler',
        allProcedures: 'Alle Behandlungen',
        allOperatories: 'Alle Räume',
        refresh: 'Aktualisieren'
      },
      
      appointments: {
        title: 'Termine',
        patient: 'Patient',
        provider: 'Behandler',
        procedure: 'Behandlung',
        operatory: 'Raum',
        date: 'Datum',
        time: 'Zeit',
        status: 'Status',
        notes: 'Notizen',
        language: 'Sprache',
        actions: 'Aktionen',
        edit: 'Bearbeiten',
        cancel: 'Stornieren',
        delete: 'Löschen',
        view: 'Details anzeigen',
        create: 'Termin erstellen',
        update: 'Termin aktualisieren',
        confirm: 'Bestätigen',
        completed: 'Als erledigt markieren',
        noShow: 'Als nicht erschienen markieren'
      },

      // Schnellerfassung
      quick: {
        nav: 'Schnelltermin',
        title: 'Schnelltermin erfassen',
        subtitle: 'In wenigen Sekunden einen Termin anlegen',
        patientName: 'Name des Patienten',
        patientNamePlaceholder: 'z. B. Maria García',
        date: 'Datum',
        time: 'Uhrzeit',
        today: 'Heute',
        tomorrow: 'Morgen',
        dayAfter: 'Übermorgen',
        language: 'Sprache des Patienten',
        moreOptions: 'Mehr Optionen',
        lessOptions: 'Weniger Optionen',
        provider: 'Behandler',
        procedure: 'Behandlung',
        operatory: 'Raum',
        email: 'E-Mail (optional)',
        phone: 'Telefon (optional)',
        notes: 'Notiz (optional)',
        notesPlaceholder: 'Was man sich merken sollte…',
        save: 'Termin speichern',
        saving: 'Speichern…',
        successTitle: 'Termin gespeichert!',
        another: 'Noch einen Termin',
        done: 'Fertig',
        durationHint: '{{minutes}} Min. · endet {{end}} Uhr',
        errMissingName: 'Bitte den Namen des Patienten eingeben',
        errNoDefaults: 'Bitte zuerst Behandler, Behandlung und Raum anlegen',
        errPastDate: 'Das Datum darf nicht in der Vergangenheit liegen'
      },

      status: {
        scheduled: 'Geplant',
        confirmed: 'Bestätigt',
        cancelled: 'Storniert',
        completed: 'Abgeschlossen',
        no_show: 'Nicht erschienen'
      },
      
      providers: {
        title: 'Behandler',
        name: 'Name',
        email: 'E-Mail',
        phone: 'Telefon',
        specialization: 'Spezialisierung',
        active: 'Aktiv',
        appointments: 'Termine',
        addProvider: 'Behandler hinzufügen',
        editProvider: 'Behandler bearbeiten'
      },
      
      procedures: {
        title: 'Behandlungen',
        name: 'Name',
        description: 'Beschreibung',
        duration: 'Dauer (min)',
        color: 'Farbe',
        active: 'Aktiv',
        addProcedure: 'Behandlung hinzufügen',
        editProcedure: 'Behandlung bearbeiten'
      },
      
      operatories: {
        title: 'Behandlungsräume',
        name: 'Name',
        description: 'Beschreibung',
        active: 'Aktiv',
        availability: 'Verfügbarkeit',
        addOperatory: 'Raum hinzufügen',
        editOperatory: 'Raum bearbeiten'
      },
      
      common: {
        save: 'Speichern',
        cancel: 'Abbrechen',
        delete: 'Löschen',
        edit: 'Bearbeiten',
        view: 'Anzeigen',
        add: 'Hinzufügen',
        search: 'Suchen',
        filter: 'Filtern',
        clear: 'Löschen',
        loading: 'Lädt...',
        error: 'Fehler',
        success: 'Erfolg',
        confirm: 'Bestätigen',
        yes: 'Ja',
        no: 'Nein',
        close: 'Schließen',
        back: 'Zurück',
        next: 'Weiter',
        previous: 'Vorherige',
        selectDate: 'Datum auswählen',
        selectTime: 'Zeit auswählen',
        required: 'Erforderlich',
        optional: 'Optional'
      },
      
      messages: {
        appointmentCreated: 'Termin erfolgreich erstellt',
        appointmentUpdated: 'Termin erfolgreich aktualisiert',
        appointmentCancelled: 'Termin erfolgreich storniert',
        appointmentDeleted: 'Termin erfolgreich gelöscht',
        confirmDelete: 'Sind Sie sicher, dass Sie diesen Termin löschen möchten?',
        confirmCancel: 'Sind Sie sicher, dass Sie diesen Termin stornieren möchten?',
        googleCalendarSync: 'Synchronisation mit Google Kalender...',
        googleCalendarSynced: 'Google Kalender erfolgreich synchronisiert',
        conflictDetected: 'Terminkonflikt erkannt',
        noConflicts: 'Keine Konflikte gefunden'
      }
    }
  },
  es: {
    translation: {
      nav: {
        dashboard: 'Panel',
        appointments: 'Citas',
        calendar: 'Calendario',
        providers: 'Doctores',
        procedures: 'Tratamientos',
        operatories: 'Salas',
        reports: 'Informes',
        settings: 'Configuración',
        logout: 'Cerrar Sesión'
      },
      
      auth: {
        login: 'Iniciar Sesión',
        username: 'Usuario',
        password: 'Contraseña',
        loginButton: 'Entrar',
        loginError: 'Usuario o contraseña incorrectos',
        welcome: 'Bienvenido a SeaDent Admin',
        pleaseLogin: 'Por favor, inicie sesión para continuar'
      },
      
      calendar: {
        title: 'Calendario de Citas',
        month: 'Mes',
        week: 'Semana',
        day: 'Día',
        today: 'Hoy',
        previous: 'Anterior',
        next: 'Siguiente',
        noEvents: 'No hay citas',
        filterBy: 'Filtrar por',
        allProviders: 'Todos los Doctores',
        allProcedures: 'Todos los Tratamientos',
        allOperatories: 'Todas las Salas',
        refresh: 'Actualizar'
      },
      
      appointments: {
        title: 'Citas',
        patient: 'Paciente',
        provider: 'Doctor',
        procedure: 'Tratamiento',
        operatory: 'Sala',
        date: 'Fecha',
        time: 'Hora',
        status: 'Estado',
        notes: 'Notas',
        language: 'Idioma',
        actions: 'Acciones',
        edit: 'Editar',
        cancel: 'Cancelar',
        delete: 'Eliminar',
        view: 'Ver Detalles',
        create: 'Crear Cita',
        update: 'Actualizar Cita',
        confirm: 'Confirmar',
        completed: 'Marcar como Completada',
        noShow: 'Marcar como No Asistió'
      },

      // Registro rápido de cita
      quick: {
        nav: 'Cita rápida',
        title: 'Nueva cita',
        subtitle: 'Crea una cita en segundos',
        patientName: 'Nombre del paciente',
        patientNamePlaceholder: 'p. ej. Maria García',
        date: 'Fecha',
        time: 'Hora',
        today: 'Hoy',
        tomorrow: 'Mañana',
        dayAfter: 'Pasado mañana',
        language: 'Idioma del paciente',
        moreOptions: 'Más opciones',
        lessOptions: 'Menos opciones',
        provider: 'Doctor',
        procedure: 'Tratamiento',
        operatory: 'Sala',
        email: 'Email (opcional)',
        phone: 'Teléfono (opcional)',
        notes: 'Nota (opcional)',
        notesPlaceholder: 'Algo que recordar…',
        save: 'Guardar cita',
        saving: 'Guardando…',
        successTitle: '¡Cita guardada!',
        another: 'Añadir otra',
        done: 'Hecho',
        durationHint: '{{minutes}} min · termina a las {{end}}',
        errMissingName: 'Introduce el nombre del paciente',
        errNoDefaults: 'Primero crea un doctor, un tratamiento y una sala',
        errPastDate: 'La fecha no puede ser en el pasado'
      },

      status: {
        scheduled: 'Programada',
        confirmed: 'Confirmada',
        cancelled: 'Cancelada',
        completed: 'Completada',
        no_show: 'No Asistió'
      },
      
      providers: {
        title: 'Doctores',
        name: 'Nombre',
        email: 'Email',
        phone: 'Teléfono',
        specialization: 'Especialización',
        active: 'Activo',
        appointments: 'Citas',
        addProvider: 'Añadir Doctor',
        editProvider: 'Editar Doctor'
      },
      
      procedures: {
        title: 'Tratamientos',
        name: 'Nombre',
        description: 'Descripción',
        duration: 'Duración (min)',
        color: 'Color',
        active: 'Activo',
        addProcedure: 'Añadir Tratamiento',
        editProcedure: 'Editar Tratamiento'
      },
      
      operatories: {
        title: 'Salas de Tratamiento',
        name: 'Nombre',
        description: 'Descripción',
        active: 'Activo',
        availability: 'Disponibilidad',
        addOperatory: 'Añadir Sala',
        editOperatory: 'Editar Sala'
      },
      
      common: {
        save: 'Guardar',
        cancel: 'Cancelar',
        delete: 'Eliminar',
        edit: 'Editar',
        view: 'Ver',
        add: 'Añadir',
        search: 'Buscar',
        filter: 'Filtrar',
        clear: 'Limpiar',
        loading: 'Cargando...',
        error: 'Error',
        success: 'Éxito',
        confirm: 'Confirmar',
        yes: 'Sí',
        no: 'No',
        close: 'Cerrar',
        back: 'Atrás',
        next: 'Siguiente',
        previous: 'Anterior',
        selectDate: 'Seleccionar Fecha',
        selectTime: 'Seleccionar Hora',
        required: 'Requerido',
        optional: 'Opcional'
      },
      
      messages: {
        appointmentCreated: 'Cita creada exitosamente',
        appointmentUpdated: 'Cita actualizada exitosamente',
        appointmentCancelled: 'Cita cancelada exitosamente',
        appointmentDeleted: 'Cita eliminada exitosamente',
        confirmDelete: '¿Está seguro de que desea eliminar esta cita?',
        confirmCancel: '¿Está seguro de que desea cancelar esta cita?',
        googleCalendarSync: 'Sincronizando con Google Calendar...',
        googleCalendarSynced: 'Google Calendar sincronizado exitosamente',
        conflictDetected: 'Conflicto de horario detectado',
        noConflicts: 'No se encontraron conflictos'
      }
    }
  },
  ru: {
    translation: {
      nav: {
        dashboard: 'Панель управления',
        appointments: 'Записи',
        calendar: 'Календарь',
        providers: 'Врачи',
        procedures: 'Процедуры',
        operatories: 'Кабинеты',
        reports: 'Отчеты',
        settings: 'Настройки',
        logout: 'Выйти'
      },
      
      auth: {
        login: 'Войти',
        username: 'Имя пользователя',
        password: 'Пароль',
        loginButton: 'Войти',
        loginError: 'Неверное имя пользователя или пароль',
        welcome: 'Добро пожаловать в SeaDent Admin',
        pleaseLogin: 'Пожалуйста, войдите для продолжения'
      },
      
      calendar: {
        title: 'Календарь записей',
        month: 'Месяц',
        week: 'Неделя',
        day: 'День',
        today: 'Сегодня',
        previous: 'Предыдущий',
        next: 'Следующий',
        noEvents: 'Нет записей',
        filterBy: 'Фильтр по',
        allProviders: 'Все врачи',
        allProcedures: 'Все процедуры',
        allOperatories: 'Все кабинеты',
        refresh: 'Обновить'
      },
      
      appointments: {
        title: 'Записи',
        patient: 'Пациент',
        provider: 'Врач',
        procedure: 'Процедура',
        operatory: 'Кабинет',
        date: 'Дата',
        time: 'Время',
        status: 'Статус',
        notes: 'Заметки',
        language: 'Язык',
        actions: 'Действия',
        edit: 'Редактировать',
        cancel: 'Отменить',
        delete: 'Удалить',
        view: 'Просмотр',
        create: 'Создать запись',
        update: 'Обновить запись',
        confirm: 'Подтвердить',
        completed: 'Отметить как выполненное',
        noShow: 'Отметить как неявку'
      },

      // Быстрая запись
      quick: {
        nav: 'Быстрая запись',
        title: 'Новая запись',
        subtitle: 'Создайте запись за несколько секунд',
        patientName: 'Имя пациента',
        patientNamePlaceholder: 'напр. Maria García',
        date: 'Дата',
        time: 'Время',
        today: 'Сегодня',
        tomorrow: 'Завтра',
        dayAfter: 'Послезавтра',
        language: 'Язык пациента',
        moreOptions: 'Больше опций',
        lessOptions: 'Меньше опций',
        provider: 'Врач',
        procedure: 'Процедура',
        operatory: 'Кабинет',
        email: 'Email (необязательно)',
        phone: 'Телефон (необязательно)',
        notes: 'Заметка (необязательно)',
        notesPlaceholder: 'Что нужно запомнить…',
        save: 'Сохранить запись',
        saving: 'Сохранение…',
        successTitle: 'Запись сохранена!',
        another: 'Добавить ещё',
        done: 'Готово',
        durationHint: '{{minutes}} мин · окончание в {{end}}',
        errMissingName: 'Введите имя пациента',
        errNoDefaults: 'Сначала добавьте врача, процедуру и кабинет',
        errPastDate: 'Дата не может быть в прошлом'
      },

      status: {
        scheduled: 'Запланировано',
        confirmed: 'Подтверждено',
        cancelled: 'Отменено',
        completed: 'Завершено',
        no_show: 'Неявка'
      },
      
      providers: {
        title: 'Врачи',
        name: 'Имя',
        email: 'Email',
        phone: 'Телефон',
        specialization: 'Специализация',
        active: 'Активный',
        appointments: 'Записи',
        addProvider: 'Добавить врача',
        editProvider: 'Редактировать врача'
      },
      
      procedures: {
        title: 'Процедуры',
        name: 'Название',
        description: 'Описание',
        duration: 'Продолжительность (мин)',
        color: 'Цвет',
        active: 'Активный',
        addProcedure: 'Добавить процедуру',
        editProcedure: 'Редактировать процедуру'
      },
      
      operatories: {
        title: 'Кабинеты',
        name: 'Название',
        description: 'Описание',
        active: 'Активный',
        availability: 'Доступность',
        addOperatory: 'Добавить кабинет',
        editOperatory: 'Редактировать кабинет'
      },
      
      common: {
        save: 'Сохранить',
        cancel: 'Отмена',
        delete: 'Удалить',
        edit: 'Редактировать',
        view: 'Просмотр',
        add: 'Добавить',
        search: 'Поиск',
        filter: 'Фильтр',
        clear: 'Очистить',
        loading: 'Загрузка...',
        error: 'Ошибка',
        success: 'Успех',
        confirm: 'Подтвердить',
        yes: 'Да',
        no: 'Нет',
        close: 'Закрыть',
        back: 'Назад',
        next: 'Далее',
        previous: 'Предыдущий',
        selectDate: 'Выбрать дату',
        selectTime: 'Выбрать время',
        required: 'Обязательно',
        optional: 'Необязательно'
      },
      
      messages: {
        appointmentCreated: 'Запись успешно создана',
        appointmentUpdated: 'Запись успешно обновлена',
        appointmentCancelled: 'Запись успешно отменена',
        appointmentDeleted: 'Запись успешно удалена',
        confirmDelete: 'Вы уверены, что хотите удалить эту запись?',
        confirmCancel: 'Вы уверены, что хотите отменить эту запись?',
        googleCalendarSync: 'Синхронизация с Google Calendar...',
        googleCalendarSynced: 'Google Calendar успешно синхронизирован',
        conflictDetected: 'Обнаружен конфликт расписания',
        noConflicts: 'Конфликтов не найдено'
      }
    }
  }
};

// Modulare Admin-Übersetzungen (src/i18n/admin/*) in die DE/ES-Ressourcen mergen.
resources.de.translation = deepMerge(resources.de.translation, buildAdminTree('de')) as typeof resources.de.translation;
resources.es.translation = deepMerge(resources.es.translation, buildAdminTree('es')) as typeof resources.es.translation;

export const ADMIN_LANGUAGES = ['de', 'es'] as const;
export type AdminLanguage = (typeof ADMIN_LANGUAGES)[number];
export const ADMIN_LANGUAGE_STORAGE_KEY = 'adminLang';

/**
 * Startsprache des Admin-Bereichs: gespeicherte Wahl → Browser-Sprache → Deutsch.
 * Eigener localStorage-Key, damit die Admin-Sprache unabhängig von der
 * öffentlichen Website (LanguageContext, Key `language`) ist.
 */
export const getInitialAdminLanguage = (): AdminLanguage => {
  try {
    const saved = localStorage.getItem(ADMIN_LANGUAGE_STORAGE_KEY);
    if (saved === 'de' || saved === 'es') return saved;
    const browser = (navigator.language || '').slice(0, 2).toLowerCase();
    if (browser === 'es') return 'es';
  } catch (error) {
    console.warn('Error reading admin language preference:', error);
  }
  return 'de';
};

i18n
  .use(initReactI18next)
  .init({
    resources,
    lng: getInitialAdminLanguage(),
    fallbackLng: 'de',

    interpolation: {
      escapeValue: false // React already escapes values
    },
  });

export default i18n; 